import Button from '@components/Button';
import { LeftIcon, StyledLoader } from '@components/Button/styles';
import Icon from '@components/Icon';
import { flexStyle, textStyle } from '@themes/mixins';
import { isDefAndNotNull } from '@utils/def';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { ColorModes } from '../../enums';

export const Root = styled.div`
  display: contents;
`;
export const Wrapper = styled.div`
  ${flexStyle({ direction: 'column' })}
  border-radius: 6px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  box-shadow: ${({ theme }) => `
    ${rgba(theme.colors.boxShadow, 0.2)} 0 0 2px 0,
    ${rgba(theme.colors.boxShadow, 0.12)} -1px 7px 12px -4px`};
`;
export const Header = styled.div`
  ${flexStyle({ direction: 'row', align: 'flex-start' })}
  background-color: ${({ theme }) => theme.colors.paper};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  padding: 12px 14px;
`;
export const Content = styled.div`
  ${flexStyle({ direction: 'column' })}
  gap: 14px;
  flex-grow: 1;
  padding: 16px 12px;
`;

export const ValidatePromptButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.turquoiseicon};
  color: ${({ theme }) => theme.colors.black};
  border-color: ${({ theme }) => theme.colors.turquoiseicon};

  & ${LeftIcon} {
    color: ${({ theme }) => theme.colors.black};
  }

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => rgba(theme.colors.turquoiseicon, 0.85)};
  }
`;

export const SaveButtonWrapper = styled.div`
  ${flexStyle({ direction: 'row' })}
  gap: 8px;
  flex-grow: 1;
`;
export const BuildScheduleWrapper = styled.div`
  ${flexStyle({ direction: 'row', justify: 'center', align: 'center' })}
  gap: 8px;
  flex: 1;
`;
export const StyledButtonBuildSchedule = styled(Button)`
  flex: 1;
`;
export const WarningScheduleButton = styled(Button)`
  color: ${({ theme }) => theme.colors.yellow};
`;
export const SaveButton = styled(Button)<{ transactionId: number | null }>`
  flex-grow: 1;

  & > ${LeftIcon} {
    margin-left: ${({ transactionId }) =>
      isDefAndNotNull(transactionId) ? '32px' : '0'};
  }

  & > ${StyledLoader} {
    margin-left: ${({ transactionId }) =>
      isDefAndNotNull(transactionId) ? '40px' : '0'};
  }
`;
export const TransactionTitleContent = styled.div`
  ${flexStyle({ direction: 'column', align: 'flex-start' })}
  gap: 8px;
  flex-grow: 1;
  padding: 4px 0 0 0;
`;
export const TransactionTitle = styled.div`
  ${flexStyle({ direction: 'row' })}
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '22px',
    textAlign: 'left',
    weight: 600,
  })};
`;
export const TransactionNumber = styled.span`
  ${flexStyle({ direction: 'column', justify: 'flex-end' })}
  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1.2,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-right: 6px;
`;
export const TransactionDescription = styled.span`
  ${flexStyle({ direction: 'row' })}
  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1.2,
    size: '13px',
    textAlign: 'left',
    weight: 400,
  })};
`;
export const Footer = styled.div`
  ${flexStyle({ direction: 'column' })}
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  flex-grow: 1;
  padding: 14px 12px;
`;
export const ValidationContainer = styled.div`
  ${flexStyle({ direction: 'column', align: 'left', justify: 'flex-start' })}
  gap: 8px;
  flex-grow: 1;
`;
export const ValidationRow = styled.div`
  ${flexStyle({ direction: 'row', align: 'center', justify: 'flex-start' })}
  flex-grow: 1;
  position: relative;
`;
export const ActionsContainer = styled.div`
  ${flexStyle({ direction: 'row', align: 'center', justify: 'flex-end' })}
  flex-grow: 1;
`;
export const Title = styled.span`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.2,
    size: '15px',
    textAlign: 'left',
    weight: 500,
  })};
`;
export const SuccessIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.green};
`;
export const WarningIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.yellow};
  position: absolute;
  left: 5px;
`;
export const ValidationTitle = styled.span`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.2,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
  padding-left: 32px;
`;
export const ModalText = styled.div`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.4,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-bottom: 32px;
`;
export const ButtonsContainer = styled.div`
  ${flexStyle({ justify: 'flex-end', wrap: 'nowrap' })};
  flex-grow: 1;
  gap: 16px;
`;
export const SettingsIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textSecondary};
`;
export const TransactionIdLabel = styled.div<{ color: ColorModes }>`
  ${flexStyle({ direction: 'row', align: 'center', justify: 'center' })}
  ${textStyle({
    color: 'white',
    lineHeight: 1,
    size: '11px',
    textAlign: 'left',
    weight: 400,
  })};
  border-radius: 4px;
  padding: 3px 6px;
  margin-left: 10px;
  text-transform: uppercase;

  ${({ color }) => {
    switch (color) {
      case ColorModes.success:
        return css`
          background-color: ${({ theme }) => theme.colors.green};
        `;
      case ColorModes.blue:
        return css`
          background-color: ${({ theme }) => theme.colors.blue};
        `;
      case ColorModes.pinkGraph:
        return css`
          background-color: ${({ theme }) => theme.colors.pinkGraph};
        `;
      case ColorModes.violet:
        return css`
          background-color: ${({ theme }) => theme.colors.violetIcon};
        `;
      case ColorModes.error:
        return css`
          background-color: ${({ theme }) => theme.colors.red};
        `;
      default: //ColorModes.gray
        return css`
          background-color: ${({ theme }) => theme.colors.grey};
        `;
    }
  }}
`;
export const ReviewStatusLabel = styled.div<{ color: ColorModes }>`
  ${flexStyle({ direction: 'row', align: 'center', justify: 'center' })}
  ${textStyle({
    color: 'white',
    lineHeight: 1,
    size: '11px',
    textAlign: 'left',
    weight: 400,
  })};
  border-radius: 4px;
  padding: 3px 6px;
  margin-left: 10px;

  ${({ color }) => {
    switch (color) {
      case ColorModes.success:
        return css`
          background-color: ${({ theme }) => theme.colors.green};
        `;
      case ColorModes.warning:
        return css`
          background-color: ${({ theme }) => theme.colors.yellow};
        `;
      case ColorModes.orange:
        return css`
          background-color: ${({ theme }) => theme.colors.orange};
        `;
      default: //ColorModes.error
        return css`
          background-color: ${({ theme }) => theme.colors.red};
        `;
    }
  }}
`;
export const TradeInfoContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-auto-rows: auto;
  gap: 8px;
  padding: 12px 16px;
`;
export const TradeInfoContainerName = styled.div`
  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1.2,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
`;
export const TradeInfoContainerValue = styled.div`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.2,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
`;
